<template>
    <div :class="{ 
            'popover': true,
            [ 'popover--' + type ]: true, 
            [ 'popover--' + state ]: true,
            [ 'popover--' + arrowClass ]: this.arrow !== false,
            [ 'popover--closeable' ]: this.closeBtn 
        }">

        <div class="popover__container" :style="containerStyle">
            <div class="popover__content">
                <slot :data="data" :target="actualTarget" />
            </div>
            <button v-if="closeBtn" class="popover__btn-close" @click="hide"><img src="../../../img/icons/cross.svg" /></button>
        </div>
        <div class="popover__arrow" data-popper-arrow></div>
    </div>
</template>

<script>
import Base from './Base';
import anime from 'animejs';
import { createPopper } from '@popperjs/core/lib/popper-lite';
import popperArrow from '@popperjs/core/lib/modifiers/arrow';
import popperOffset from '@popperjs/core/lib/modifiers/offset';

export default {
    extends: Base,
    props: {
        initialState: {
            type: String,
            default: 'visible'
        },
        target: {
            type: String | Object,
            default() {
                return null;
            }
        },
        relativeTo: {
            default: () => { return document.getElementsByTagName('html')[0]; }
        },
        type: {
            type: String,
            default: 'default'
        },
        data: {
            type: Object
        },
        position: {
            type: String,
            default: 'left'
        },
        width: {
            type: String
        },
        maxWidth: {
            type: String
        },
        showOnMounted: {
            type: Boolean,
            default: true
        },
        showOnUpdated: {
            type: Boolean,
            default: true
        },
        hideOnOutsideClick: {
            type: Boolean,
            default: true
        },
        toggleOn: {
            type: String | Array
        },
        toggleOnTarget: {
            type: Object | String
        },
        closeBtn: {
            type: Boolean,
            default: true
        },
        arrow: {
            type: String | Boolean,
            default: true
        },
        offset: {
            type: Array,
            default() {
                return [ 0, 5 ];
            }
        },
        value: {
            type: Boolean
        }
    },
    data() {
        return {
            state: 'hidden',
            actualTarget: this.target
        }
    },
    computed: {
        containerStyle() {
            return {
                width: this.width,
                maxWidth: this.maxWidth
            };
        },
        arrowClass() {
            return 'arrow-' + (this.arrow === true ? this.type : this.arrow);
        }
    },
    created() {
        this.ignoreUpdate = false;
    },
    mounted() {
        if(!this.actualTarget) {
            this.actualTarget = this.$el.parentElement;
        }

        if(document.readyState == 'complete') {
            this.init();
        } else {
            window.addEventListener('load', () => {
                this.init();
            });
        }

        const target = this.getTarget();
        let toggleOn = this.toggleOn;

        if(toggleOn && (this.toggleOnTarget || target)) {
            const toggleOnTarget = this.toggleOnTarget || target;

            if(!Array.isArray(toggleOn)) {
                toggleOn = [ toggleOn ];
            }

            for(const on of toggleOn) {
                this.findElement(toggleOnTarget).addEventListener(on, () => {
                    this.toggle();
                });
            }
        }

        if(this.hideOnOutsideClick) {
            target.addEventListener('click', event => {
                event.stopPropagation();
            });
            this.$el.addEventListener('click', event => {
                event.stopPropagation();
            });

            this.onBodyClick = () => {
                this.hide();
            }

            document.body.addEventListener('click', this.onBodyClick);
        }
    },
    updated() {
        if(!this.ignoreUpdate && this.showOnUpdated && this.state == 'hidden') {
            this.show();
        } else {
            if(this.state == 'visible') {
                if(this.popper) {
                    this.destroyPopper();
                    this.createPopper();
                } else {
                    this.createPopper();
                }
            } else if(this.state == 'hidden' && this.popper) {
                this.destroyPopper();
            }
        }

        this.ignoreUpdate = false;
    },
    beforeDestroy() {
        this.destroyPopper();

        if(this.onBodyClick) {
            document.body.removeEventListener('click', this.onBodyClick);
        }
    },
    methods: {
        init() {
            if(this.showOnMounted) {
                this.show();
            }
        },
        show() {
            this.state = 'visible';
        },
        hide() {
            this.state = 'hidden';

            this.ignoreUpdate = true;

            this.$emit('close');
            this.$emit('input', !this.value);
        },
        toggle() {
            if(this.state == 'visible') {
                this.hide();
            } else {
                this.show();
            }
        },
        setState(state) {
            if(state == 'visible') {
                this.show();
            } else if(state == 'hidden') {
                this.hide();
            } else {
                this.state = state;
            }
        },
        createPopper() {
            let modifiers = [];

            if(this.arrow) {
                modifiers.push(popperArrow);
            }
            if(this.offset) {
                popperOffset.options = { offset: this.offset };
                modifiers.push(popperOffset);
            }

            this.popper = createPopper(this.getTarget(), this.$el, {
                modifiers: modifiers,
                placement: this.position
            });
        },
        destroyPopper() {
            if(this.popper) {
                this.popper.destroy();
                this.popper = null;
            }
        },
        findElement(expression, relativeTo = null) {
            if(typeof expression != 'string') {
                return expression;
            } else {
                if(!relativeTo) {
                    relativeTo = document;
                }

                if(expression == '$parent') {
                    return this.$el.parentElement;
                } else {
                    return relativeTo.querySelector(expression);
                }
            }
        },
        getTarget() {
            return this.findElement(this.actualTarget, this.getRelativeTo());
        },
        getRelativeTo() {
            return this.findElement(this.relativeTo);
        }
    }
}
</script>

<style lang="scss">
.popover {
    position: absolute;
    display: block;
    transform-style: preserve-3d;
    z-index: 5;

    &__container {
        position: relative;
        overflow: hidden;
    }

    &--hidden {
        display: none;
    }

    &__btn-close {
        position: absolute;
        right: 5px;
        top: 5px;
        padding: 0;
        line-height: 0;
        width: 8px;
        height: 8px;
    }
    
    &__arrow,
    &__arrow::before {
        position: absolute;
        width: 8px;
        height: 8px;
        z-index: -1;
    }

    &__arrow::before {
        content: '';
        transform: rotate(45deg);
    }

    [data-popper-placement^='top'] > &__arrow {
        bottom: -4px;
    }

    [data-popper-placement^='bottom'] > &__arrow {
        top: -4px;
    }

    [data-popper-placement^='left'] > &__arrow {
        right: -4px;
    }

    [data-popper-placement^='right'] > &__arrow {
        left: -4px;
    }
}
</style>